import { Navigate, createBrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { Employee } from '../Employee';
import { RouterErrorComponent } from '../common/ErrorComponent';
import { OAuthCallback } from '../common/oauth/OAuthCallback';
import { PrivacyPolicyDetail } from '../common/privacyPolicy/detail';
import { ChangePassword } from '../common/sign/changePassword';
import { ExpiredPasswordResetLink } from '../common/sign/changePassword/ExpiredPasswordResetLink';
import { ConfirmMail } from '../common/sign/confirmMail/ConfirmMail';
import { RegisteredUser } from '../common/sign/registeredUser/RegisteredUser';
import { SearchPassword } from '../common/sign/searchPassword/SearchPassword';
import { SelectTenant } from '../common/sign/selectTenant/SelectTenant';
import { SignIn } from '../common/sign/signIn/SignIn';
import { SsoLoginPage } from '../common/sign/signIn/SsoLoginPage';
import { Store } from '../common/store';
import { Products as StoreProducts } from '../common/store/products';
import { ProductsInCategory } from '../common/store/products/ProductsInCategory';
import { ProductDetail } from '../common/store/products/productDetail';
import { ProviderIntroduction } from '../common/store/providerStore/ProviderIntroduction';
import { ProviderStore } from '../common/store/providerStore/ProviderStore';
import { ProviderStoreLayout } from '../common/store/providerStore/ProviderStoreLayout';
import { ProviderStoreProductsInCategory } from '../common/store/providerStore/ProviderStoreProductsInCategory';
import { TenantRequest } from '../common/tenantRequest';
import { PageNotFound } from '../manager/pageNotFound';
import { Introduction } from './introduction';
import { Subscription } from './subscription';
import { ApplyDetail } from './subscription/applyHistory/detail';
import { ApplyHistoryList } from './subscription/applyHistory/list';
import { ApprovalMgmtList } from './subscription/approvalMgmt';
import { ApprovalDetail } from './subscription/approvalMgmt/detail';
import { ApprovalCompleted } from './subscription/approvalMgmt/list/ApprovalCompleted';
import { ApprovalPending } from './subscription/approvalMgmt/list/ApprovalPending';
import { ProductInCompany } from './subscription/productsInCompany/list/ProductInCompany';
import { ProductInCompanyList } from './subscription/productsInCompany/list/ProductInCompanyList';
import { SubscribingList } from './subscription/subscribing/list';
import { Products } from './subscription/subscribing/list/Products';
import { TechnicalSupportDetail } from './technicalSupport/TechnicalSupportDetail';
import { TechnicalSupportList } from './technicalSupport/TechnicalSupportList';

type EmployeeRouterProps = {
  enabledIntroductionPage: boolean;
  enabledOnlyUseLoginPage: boolean;
  enabledPrivacyPolicy: boolean;
};

const createEmployeeRouter = ({
  enabledIntroductionPage,
  enabledOnlyUseLoginPage,
  enabledPrivacyPolicy,
}: EmployeeRouterProps) =>
  createBrowserRouter([
    {
      path: '/stores/:providerDomain',
      element: <ProviderStoreLayout />,
      errorElement: (
        <ErrorBoundary>
          <RouterErrorComponent text="에러가 발생했습니다." />
        </ErrorBoundary>
      ),
      children: [
        ...(enabledIntroductionPage ? [{ path: 'introduction', element: <ProviderIntroduction /> }] : []),
        { path: 'tenant-request', element: <TenantRequest /> },
        {
          element: <ProviderStore />,
          children: [
            { path: 'category/:name', element: <ProviderStoreProductsInCategory /> },
            { path: 'product/:id', element: <ProductDetail /> },
            { index: true, element: <ProviderStoreProductsInCategory /> },
            { path: '*', element: <ProviderStoreProductsInCategory /> },
          ],
        },
      ],
    },
    {
      path: '/',
      element: <Employee enabledOnlyUseLoginPage={enabledOnlyUseLoginPage} />,
      errorElement: (
        <ErrorBoundary>
          <RouterErrorComponent text="에러가 발생했습니다." />
        </ErrorBoundary>
      ),
      children: [
        { index: true, element: <Navigate to={enabledOnlyUseLoginPage ? '/login' : '/store'} replace /> },
        // INTRODUCTION_PAGE가 true인 경우에만 introduction 라우트 추가
        ...(enabledIntroductionPage ? [{ path: 'introduction', element: <Introduction /> }] : []),
        // ONLY_USE_LOGIN_PAGE가 true인 경우에는 store, subscriptions 라우트 추가하지 않음
        ...(enabledOnlyUseLoginPage
          ? []
          : [
              {
                path: 'store/*',
                element: <Store />,
                children: [
                  { path: 'softwares', element: <StoreProducts type="Software" /> },
                  { path: 'hardwares', element: <StoreProducts type="Hardware" /> },
                  { path: 'category/:name', element: <ProductsInCategory /> },
                  { path: 'product/:id', element: <ProductDetail /> },
                  { path: '*', element: <ProductsInCategory /> },
                ],
              },
              {
                path: 'subscriptions/*',
                element: <Subscription />,
                children: [
                  {
                    path: 'list/*',
                    element: <SubscribingList />,
                    children: [
                      { path: 'software', element: <Products type="Software" /> },
                      { path: 'hardware', element: <Products type="Hardware" /> },
                      { path: ':rootCategoryId', element: <Products /> },
                      { path: '*', element: <Products /> },
                    ],
                  },
                  { path: 'usage-apply', element: <ApplyHistoryList /> },
                  { path: 'usage-apply/:id', element: <ApplyDetail /> },
                  {
                    path: 'approval-mgmt/*',
                    element: <ApprovalMgmtList />,
                    children: [
                      { index: true, element: <Navigate to="pending" replace /> },
                      { path: 'pending', element: <ApprovalPending /> },
                      { path: 'completed', element: <ApprovalCompleted /> },
                      { path: '*', element: <ApprovalPending /> },
                    ],
                  },
                  {
                    path: 'approval-mgmt/detail/:approvalId',
                    element: <ApprovalDetail />,
                  },
                  { path: 'technical-support', element: <TechnicalSupportList /> },
                  {
                    path: 'technical-support/:id',
                    element: <TechnicalSupportDetail />,
                  },
                  {
                    path: 'subscriptions-company/*',
                    element: <ProductInCompanyList />,
                    children: [
                      {
                        path: 'software',
                        element: <ProductInCompany type="Software" />,
                      },
                      {
                        path: 'hardware',
                        element: <ProductInCompany type="Hardware" />,
                      },
                      { path: ':rootCategoryId', element: <ProductInCompany /> },
                      { path: '*', element: <ProductInCompany /> },
                    ],
                  },
                  { path: '*', element: <Navigate to="list" replace /> },
                ],
              },
            ]),
        { path: 'tenant-request', element: <TenantRequest /> },
        { path: 'select-tenant', element: <SelectTenant /> },
        { path: 'change-password', element: <ChangePassword /> },
        { path: 'change-password/:token', element: <ChangePassword /> },
        { path: 'search-password', element: <SearchPassword /> },
        { path: 'confirm-mail/:email', element: <ConfirmMail /> },
        { path: 'registered-user', element: <RegisteredUser /> },
        {
          path: 'expired-password-reset-link',
          element: <ExpiredPasswordResetLink />,
        },
        { path: 'login', element: <SignIn /> },
        { path: 'sso-login', element: <SsoLoginPage /> },
        { path: 'error', element: <RouterErrorComponent /> },
        ...(enabledPrivacyPolicy
          ? [
              { path: 'privacy-policy', element: <PrivacyPolicyDetail /> },
              { path: 'privacy-policy/:version', element: <PrivacyPolicyDetail /> },
            ]
          : []),
        { path: 'oauth-callback', element: <OAuthCallback /> },
        { path: '*', element: <PageNotFound /> },
      ],
    },
  ]);

export default createEmployeeRouter;
